import { Box, Chip, Slider } from '@mui/material';
import { useIntl } from 'react-intl';
import { thinRailSliderSx } from '~/components/toolbar/tools/ContouringTools/ToolTraySlider';
import { IntensityThresholdViewModel } from './useIntensityThreshold';

/** The threshold's range as read out, such as `-500 – 1710 HU`. */
export function thresholdRangeText({
    sliderValue,
    formatValue,
    unitLabel,
}: Pick<
    IntensityThresholdViewModel,
    'sliderValue' | 'formatValue' | 'unitLabel'
>): string {
    return `${formatValue(sliderValue[0])} – ${formatValue(sliderValue[1])} ${unitLabel}`;
}

/**
 * The settings of the intensity-threshold constraint, which deposits contour
 * only on voxels within an intensity range (HU for CT, SUVbw for PET): the
 * range and its presets, which the section's header reads out with
 * {@link thresholdRangeText}. It is presentational: the view-model
 * ({@link useIntensityThreshold}) supplies state, unit handling, and handlers.
 * Shows nothing while the threshold cannot apply or has no range.
 */
export default function IntensityThresholdSection({
    available,
    enabled,
    hasRange,
    displayMin,
    displayMax,
    sliderValue,
    sliderStep,
    unitLabel,
    formatValue,
    presets,
    activePresetIndex,
    onRangeChange,
    onApplyPreset,
}: IntensityThresholdViewModel) {
    const intl = useIntl();

    if (!available || !hasRange) {
        return null;
    }
    return (
        <>
            <Slider
                // Held while the threshold is off, as the other modifiers'
                // settings are.
                disabled={!enabled}
                size="small"
                value={sliderValue}
                min={displayMin}
                max={displayMax}
                step={sliderStep}
                disableSwap
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${formatValue(v)} ${unitLabel}`}
                onChange={(_, value) => onRangeChange(value)}
                aria-label={intl.formatMessage({ id: 'thresholdRangeAria' })}
                sx={thinRailSliderSx}
            />
            {presets.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {presets.map((preset, idx) => (
                        // The chip whose range matches the current selection
                        // reads as chosen, so the active preset is obvious
                        // at a glance.
                        <Chip
                            disabled={!enabled}
                            key={preset.labelId}
                            variant="tag"
                            color={
                                idx === activePresetIndex
                                    ? 'primary'
                                    : 'default'
                            }
                            label={intl.formatMessage({ id: preset.labelId })}
                            onClick={() => onApplyPreset(idx)}
                        />
                    ))}
                </Box>
            )}
        </>
    );
}
